import { EMAIL_PATTERN, isEmailDomainAllowed } from '@psyomics/ui-common';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../atoms/button/button';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { PhoneInput } from '../../../atoms/phone-input/phone-input';

import { RegistrationMethod, UsernameFieldProps, UsernameInputProps } from './types';

const useUsernameFieldRules = (isEmailInput: boolean, isValid: boolean, allowedEmailDomains?: string[]) => {
  const { t } = useTranslation();
  return isEmailInput
    ? {
        required: t('auth.common.errors.emailRequired'),
        pattern: {
          value: EMAIL_PATTERN,
          message: t('auth.common.errors.emailInvalid'),
        },
        validate: allowedEmailDomains?.length
          ? (value: string) =>
              isEmailDomainAllowed(value, allowedEmailDomains) || t('auth.common.errors.emailDomainNotAllowed')
          : undefined,
      }
    : {
        required: t('auth.common.errors.phoneNumberRequired'),
        pattern: undefined,
        validate: (value: string) => {
          if (value?.trim().length && !isValid) return t('auth.common.errors.phoneNumberInvalid');
          return true;
        },
      };
};

const UsernameInput = ({
  control,
  registrationMethod,
  error,
  setError,
  clearErrors,
  allowedEmailDomains,
}: UsernameInputProps) => {
  const { t } = useTranslation();
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [isPhoneCountryValid, setIsPhoneCountryValid] = useState(true);

  const isEmailInput = registrationMethod === 'email';

  const usernamePlaceholder = t(isEmailInput ? 'auth.common.emailPlaceholder' : 'auth.common.mobilePlaceholder');
  const usernameRules = useUsernameFieldRules(
    isEmailInput,
    isPhoneNumberValid && isPhoneCountryValid,
    allowedEmailDomains
  );

  useEffect(() => {
    !isEmailInput && !isPhoneCountryValid
      ? setError('username', { message: t('auth.common.errors.phoneNumberInvalid') })
      : clearErrors('username');
  }, [clearErrors, setError, isPhoneCountryValid, registrationMethod, isEmailInput, t]);

  return (
    <Controller
      control={control}
      name="username"
      render={({ field: { name, onBlur, onChange, ref, value } }) =>
        isEmailInput ? (
          <Input
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={usernamePlaceholder}
            ref={ref}
            type="email"
            value={value}
            variant={error ? 'error' : 'default'}
          />
        ) : (
          <div className="w-full">
            <PhoneInput
              inputMode="tel"
              name={name}
              onBlur={onBlur}
              onChangeNumber={onChange}
              placeholder={usernamePlaceholder}
              setIsValidCountry={setIsPhoneCountryValid}
              setIsValidNumber={setIsPhoneNumberValid}
              value={value}
              variant={error ? 'error' : 'default'}
            />
          </div>
        )
      }
      rules={usernameRules}
    />
  );
};

const UsernameField = ({
  clearErrors,
  control,
  error,
  register,
  setValue,
  defaultMethod = 'email',
  showRegistrationMethodToggle,
  switchToEmailLabelKey,
  switchToMobileLabelKey,
  setError,
  allowedEmailDomains,
}: UsernameFieldProps) => {
  const { t } = useTranslation();
  const [registrationMethod, setRegistrationMethod] = useState<RegistrationMethod>(defaultMethod);

  const usernameLabel = t(registrationMethod === 'email' ? 'auth.common.emailLabel' : 'auth.common.mobileLabel');
  const toggleLabel = t(registrationMethod === 'email' ? switchToMobileLabelKey : switchToEmailLabelKey);

  useEffect(() => {
    setValue('registrationMethod', registrationMethod);
  }, [registrationMethod, setValue]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="username">{usernameLabel}</Label>
      <UsernameInput
        allowedEmailDomains={allowedEmailDomains}
        clearErrors={clearErrors}
        control={control}
        error={error}
        registrationMethod={registrationMethod}
        setError={setError}
      />
      <input type="hidden" {...register('registrationMethod')} />
      {showRegistrationMethodToggle && (
        <Button
          appearance="secondary"
          onClick={(e) => {
            e.preventDefault();
            setRegistrationMethod(registrationMethod === 'email' ? 'phone' : 'email');
          }}
          type="button"
        >
          {toggleLabel}
        </Button>
      )}
    </div>
  );
};

export { UsernameField };
